import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ArrowRight, MapPin, Clock, ShoppingBag, Store,
  ShieldCheck, Loader2, User, Phone, Mail, CheckCircle2,
} from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * CheckoutPage — TBC 3-stage checkout that hands off to Stripe hosted.
 *   Step 1 Pickup   — pick date + time from seller's pickup windows
 *   Step 2 Details  — buyer contact info (prefilled from account)
 *   Step 3 Review   — see everything + tip picker + Place Order → Stripe
 *
 * Pickup slots are generated client-side from the seller's
 * `fulfillment.pickup_windows` + `pickup_lead_minutes`. No per-slot
 * capacity backend — first pass is availability-only. Delivery is
 * intentionally not surfaced yet.
 */

const DOW = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function hhmmToMinutes(hhmm) {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function generateSlotsForDate(date, window, leadMinutes, existingNow = Date.now()) {
  if (!window || window.enabled === false || !window.open || !window.close) return [];
  const openMin = hhmmToMinutes(window.open);
  const closeMin = hhmmToMinutes(window.close);
  if (closeMin <= openMin) return [];
  const slots = [];
  const base = new Date(date);
  base.setHours(0, 0, 0, 0);
  const nowFloor = Math.ceil((existingNow + leadMinutes * 60_000) / (15 * 60_000)) * (15 * 60_000);
  for (let m = openMin; m + 15 <= closeMin; m += 15) {
    const slotTs = base.getTime() + m * 60_000;
    if (slotTs < nowFloor) continue;
    slots.push(new Date(slotTs));
  }
  return slots;
}

function fmtDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function fmtTime(d) {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function CheckoutPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [pickupDate, setPickupDate] = useState(null);   // Date at 00:00
  const [pickupSlot, setPickupSlot] = useState(null);   // Date
  const [details, setDetails] = useState({ name: "", phone: "", email: "" });
  const [tipMode, setTipMode] = useState("none");       // "none" | 10 | 15 | 20 | "custom"
  const [tipCustom, setTipCustom] = useState("");
  const [placing, setPlacing] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cart`, { withCredentials: true });
      const c = r.data?.cart;
      setCart(c);
      if (c?.items?.length) {
        const bizId = c.items[0].product?.business_id;
        if (bizId) {
          const b = await axios.get(`${API}/businesses/${bizId}`);
          setSeller(b.data);
        }
      }
    } catch (e) {
      toast.error("Couldn't load your cart");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart]);
  useEffect(() => {
    if (user) setDetails((d) => ({
      ...d,
      name: d.name || user.name || "",
      email: d.email || user.email || "",
    }));
  }, [user]);

  const fulfillment = seller?.fulfillment || {};
  const pickupEnabled = fulfillment.pickup_enabled !== false;
  const tippingEnabled = !!fulfillment.tipping_enabled;
  const leadMinutes = Number(fulfillment.pickup_lead_minutes || 30);
  const pickupWindows = fulfillment.pickup_windows || {};

  // Next 7 days that have a window enabled + at least one future slot.
  const availableDates = useMemo(() => {
    const days = [];
    const start = new Date();
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(start.getTime() + i * 86400_000);
      d.setHours(0, 0, 0, 0);
      const key = DOW[d.getDay()];
      const slots = generateSlotsForDate(d, pickupWindows[key], leadMinutes);
      if (slots.length) days.push({ date: d, key, slots });
    }
    return days;
  }, [pickupWindows, leadMinutes]);

  const activeSlots = useMemo(() => {
    if (!pickupDate) return [];
    const found = availableDates.find((d) => d.date.getTime() === pickupDate.getTime());
    return found?.slots || [];
  }, [pickupDate, availableDates]);

  // Auto-select the first available date so the buyer immediately sees
  // time slots without an extra tap. If the seller edits their hours
  // later we keep the current selection if it's still valid.
  useEffect(() => {
    if (!pickupDate && availableDates.length > 0) {
      setPickupDate(availableDates[0].date);
    }
  }, [availableDates, pickupDate]);

  const subtotal = Number(cart?.total || 0);
  const tipAmount = useMemo(() => {
    if (!tippingEnabled) return 0;
    if (tipMode === "custom") return Math.max(0, Number(tipCustom) || 0);
    if (tipMode === "none") return 0;
    return Math.round(subtotal * (Number(tipMode) / 100) * 100) / 100;
  }, [tipMode, tipCustom, subtotal, tippingEnabled]);
  const grandTotal = Math.round((subtotal + tipAmount) * 100) / 100;

  const step1Ready = pickupEnabled && !!pickupSlot;
  const step2Ready = !!details.name.trim() && !!details.phone.trim() && !!details.email.trim();

  const placeOrder = async () => {
    if (!user) { login(); return; }
    setPlacing(true);
    try {
      const body = {
        origin_url: window.location.origin,
        fulfillment_type: "pickup",
        pickup_time: pickupSlot ? pickupSlot.toISOString() : null,
        customer_details: details,
        tip_amount: tipAmount,
      };
      const r = await axios.post(`${API}/checkout`, body, { withCredentials: true });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Stripe didn't return a checkout URL.");
        setPlacing(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Loader2 className="w-8 h-8 mx-auto text-tbc-gold animate-spin" />
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <ShoppingBag className="w-12 h-12 mx-auto text-gray-300" />
          <h1 className="mt-3 font-heading text-2xl text-tbc-navy">Sign in to check out</h1>
          <Button className="mt-6 bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6" onClick={login}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }
  if (!cart || !cart.items?.length) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <ShoppingBag className="w-12 h-12 mx-auto text-gray-300" />
          <h1 className="mt-3 font-heading text-2xl text-tbc-navy">Your cart is empty</h1>
          <Link to="/directory"><Button className="mt-6 bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6">Browse businesses</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Link to="/cart" className="inline-flex items-center text-sm text-gray-600 hover:text-tbc-navy font-body" data-testid="checkout-back-to-cart">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to cart
        </Link>

        <div className="mt-4 flex items-center gap-2 sm:gap-4" data-testid="checkout-stepper">
          {[["1", "Pickup"], ["2", "Details"], ["3", "Review"]].map(([num, label]) => {
            const n = Number(num);
            const active = step === n;
            const done = step > n;
            return (
              <div key={num} className="flex items-center gap-2">
                <span
                  className={
                    "w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading " +
                    (done ? "bg-tbc-gold text-tbc-navy" : active ? "bg-tbc-navy text-white" : "bg-gray-100 text-gray-400")
                  }
                >
                  {done ? <CheckCircle2 className="w-4 h-4" /> : num}
                </span>
                <span className={"text-sm font-body " + (active ? "text-tbc-navy font-semibold" : "text-gray-500")}>
                  {label}
                </span>
                {n < 3 && <span className="w-6 sm:w-10 h-px bg-gray-200" />}
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
          <div>
            {step === 1 && (
              <StepPickup
                seller={seller}
                pickupEnabled={pickupEnabled}
                availableDates={availableDates}
                pickupDate={pickupDate}
                setPickupDate={(d) => { setPickupDate(d); setPickupSlot(null); }}
                activeSlots={activeSlots}
                pickupSlot={pickupSlot}
                setPickupSlot={setPickupSlot}
              />
            )}
            {step === 2 && (
              <StepDetails details={details} setDetails={setDetails} />
            )}
            {step === 3 && (
              <StepReview
                cart={cart}
                seller={seller}
                pickupSlot={pickupSlot}
                details={details}
                tippingEnabled={tippingEnabled}
                tipMode={tipMode}
                setTipMode={setTipMode}
                tipCustom={tipCustom}
                setTipCustom={setTipCustom}
                subtotal={subtotal}
                tipAmount={tipAmount}
                grandTotal={grandTotal}
                onEditPickup={() => setStep(1)}
                onEditDetails={() => setStep(2)}
              />
            )}

            <div className="mt-6 flex items-center justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1.5 text-sm text-tbc-navy hover:text-tbc-gold"
                  data-testid="checkout-back"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : <span />}
              {step < 3 ? (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={(step === 1 && !step1Ready) || (step === 2 && !step2Ready)}
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6"
                  data-testid="checkout-next"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={placeOrder}
                  disabled={placing || !step1Ready || !step2Ready}
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-6 font-semibold"
                  data-testid="checkout-place-order"
                >
                  {placing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  Place Order · ${grandTotal.toFixed(2)}
                </Button>
              )}
            </div>
          </div>

          <OrderSummary cart={cart} tipAmount={tipAmount} grandTotal={grandTotal} tippingEnabled={tippingEnabled} />
        </div>

        <p className="mt-8 text-xs text-gray-400 font-body text-center inline-flex items-center gap-1.5 w-full justify-center">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout powered by Stripe.
        </p>
      </div>

      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------- */

function StepPickup({ seller, pickupEnabled, availableDates, pickupDate, setPickupDate, activeSlots, pickupSlot, setPickupSlot }) {
  return (
    <section data-testid="step-pickup">
      <h2 className="font-heading text-xl text-tbc-navy">Pickup time</h2>
      {seller && (
        <div className="mt-2 flex items-start gap-2 text-sm text-gray-600 font-body">
          <MapPin className="w-4 h-4 text-tbc-gold mt-0.5" />
          <div>
            <p className="text-tbc-navy font-semibold">{seller.name}</p>
            <p>
              {(seller.fulfillment?.pickup_address || seller.address || "").trim() || "Pickup address on file with the seller"}
            </p>
            {seller.city && seller.state && <p>{seller.city}, {seller.state}</p>}
          </div>
        </div>
      )}

      {!pickupEnabled ? (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="pickup-unavailable">
          <p className="font-heading text-tbc-navy">Pickup isn't set up for this seller yet.</p>
          <p className="mt-1 text-sm text-gray-600 font-body">
            Please contact the seller directly to arrange fulfillment.
          </p>
        </div>
      ) : availableDates.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="pickup-no-slots">
          <p className="font-heading text-tbc-navy">No open pickup windows this week.</p>
          <p className="mt-1 text-sm text-gray-600 font-body">The seller hasn't published hours yet — try again later.</p>
        </div>
      ) : (
        <>
          <p className="mt-5 text-xs uppercase tracking-widest text-tbc-navy/60 font-body">Date</p>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="pickup-date-list">
            {availableDates.map((d) => {
              const on = pickupDate?.getTime() === d.date.getTime();
              return (
                <button
                  key={d.date.toISOString()}
                  type="button"
                  onClick={() => setPickupDate(d.date)}
                  className={
                    "whitespace-nowrap rounded-2xl border px-4 py-2.5 text-left transition-all " +
                    (on ? "bg-tbc-navy border-tbc-navy text-white" : "bg-white border-gray-200 text-tbc-navy hover:border-tbc-gold/60")
                  }
                  data-testid={`pickup-date-${d.date.toISOString().slice(0,10)}`}
                >
                  <p className="text-xs uppercase tracking-widest opacity-70">{fmtDay(d.date).split(",")[0]}</p>
                  <p className="text-sm font-heading mt-0.5">{d.date.getDate()}</p>
                </button>
              );
            })}
          </div>

          {pickupDate && (
            <>
              <p className="mt-6 text-xs uppercase tracking-widest text-tbc-navy/60 font-body">Time</p>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="pickup-slot-list">
                {activeSlots.map((s) => {
                  const on = pickupSlot?.getTime() === s.getTime();
                  return (
                    <button
                      key={s.toISOString()}
                      type="button"
                      onClick={() => setPickupSlot(s)}
                      className={
                        "rounded-full border px-3 py-2 text-sm font-body transition-colors " +
                        (on ? "bg-tbc-gold border-tbc-gold text-tbc-navy font-semibold" : "bg-white border-gray-200 text-tbc-navy hover:border-tbc-gold/60")
                      }
                      data-testid={`pickup-slot-${s.getTime()}`}
                    >
                      {fmtTime(s)}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function StepDetails({ details, setDetails }) {
  const patch = (p) => setDetails((d) => ({ ...d, ...p }));
  return (
    <section data-testid="step-details">
      <h2 className="font-heading text-xl text-tbc-navy">Your details</h2>
      <p className="mt-1 text-sm text-gray-500 font-body">We'll use this to send pickup updates.</p>
      <div className="mt-5 grid grid-cols-1 gap-4">
        <Field label="Full name" icon={User}>
          <input
            type="text"
            value={details.name}
            onChange={(e) => patch({ name: e.target.value })}
            className="w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-tbc-gold/40"
            data-testid="details-name"
            required
          />
        </Field>
        <Field label="Phone" icon={Phone}>
          <input
            type="tel"
            value={details.phone}
            onChange={(e) => patch({ phone: e.target.value })}
            className="w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-tbc-gold/40"
            data-testid="details-phone"
            required
          />
        </Field>
        <Field label="Email" icon={Mail}>
          <input
            type="email"
            value={details.email}
            onChange={(e) => patch({ email: e.target.value })}
            className="w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-tbc-gold/40"
            data-testid="details-email"
            required
          />
        </Field>
      </div>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-tbc-navy/60 font-body inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function StepReview({ cart, seller, pickupSlot, details, tippingEnabled, tipMode, setTipMode, tipCustom, setTipCustom, subtotal, tipAmount, grandTotal, onEditPickup, onEditDetails }) {
  return (
    <section data-testid="step-review" className="space-y-5">
      <h2 className="font-heading text-xl text-tbc-navy">Review order</h2>

      <ReviewBlock title="Pickup" onEdit={onEditPickup}>
        <p className="text-sm font-body text-tbc-navy inline-flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-tbc-gold" />
          {pickupSlot ? `${fmtDay(pickupSlot)} · ${fmtTime(pickupSlot)}` : "—"}
        </p>
        {seller && (
          <p className="mt-1 text-xs text-gray-500 font-body inline-flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" /> {seller.name}
          </p>
        )}
      </ReviewBlock>

      <ReviewBlock title="Details" onEdit={onEditDetails}>
        <p className="text-sm font-body text-tbc-navy">{details.name}</p>
        <p className="mt-0.5 text-xs text-gray-500 font-body">{details.phone} · {details.email}</p>
      </ReviewBlock>

      {tippingEnabled && (
        <ReviewBlock title="Tip">
          <div className="flex flex-wrap gap-2">
            {[["none", "No tip"], [10, "10%"], [15, "15%"], [20, "20%"], ["custom", "Custom"]].map(([key, label]) => {
              const on = tipMode === key || (key === 10 && tipMode === 10);
              return (
                <button
                  key={String(key)}
                  type="button"
                  onClick={() => setTipMode(key)}
                  className={
                    "rounded-full border px-3 py-1.5 text-xs font-body transition-colors " +
                    (on ? "bg-tbc-navy border-tbc-navy text-white" : "bg-white border-gray-200 text-tbc-navy hover:border-tbc-gold/60")
                  }
                  data-testid={`tip-mode-${key}`}
                >
                  {label}
                </button>
              );
            })}
            {tipMode === "custom" && (
              <input
                type="number"
                step="0.01"
                min={0}
                value={tipCustom}
                onChange={(e) => setTipCustom(e.target.value)}
                placeholder="0.00"
                className="w-24 h-8 rounded-full border border-gray-200 px-3 text-sm font-body"
                data-testid="tip-custom"
              />
            )}
          </div>
        </ReviewBlock>
      )}
    </section>
  );
}

function ReviewBlock({ title, onEdit, children }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm text-tbc-navy">{title}</h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-tbc-navy hover:text-tbc-gold font-body underline underline-offset-2"
          >
            Edit
          </button>
        )}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function OrderSummary({ cart, tipAmount, grandTotal, tippingEnabled }) {
  return (
    <aside className="rounded-2xl bg-tbc-cream p-5 h-fit sticky top-6" data-testid="checkout-summary">
      <h3 className="font-heading text-lg text-tbc-navy">Order</h3>
      <div className="mt-3 space-y-3">
        {cart.items.map((it) => (
          <div key={`${it.product_id}::${it.variant_key || "base"}`} className="flex gap-3">
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-white flex-shrink-0">
              <img src={resolveAssetUrl(it.product?.image_url) || ""} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm text-tbc-navy truncate">{it.product?.name}</p>
              <p className="text-[11px] text-gray-500 font-body">Qty {it.quantity}</p>
            </div>
            <p className="font-heading text-sm text-tbc-navy">${Number(it.subtotal || 0).toFixed(2)}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-tbc-gold/20 space-y-1.5 text-sm font-body">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span data-testid="summary-subtotal">${Number(cart.total || 0).toFixed(2)}</span>
        </div>
        {tippingEnabled && (
          <div className="flex justify-between text-gray-600">
            <span>Tip</span>
            <span data-testid="summary-tip">${tipAmount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-heading text-tbc-navy text-base pt-1">
          <span>Total</span>
          <span data-testid="summary-total">${grandTotal.toFixed(2)}</span>
        </div>
      </div>
    </aside>
  );
}
